// PowerSync Connector — связывает PowerSync клиент с нашим сервером.
//
//   - fetchCredentials() — через `jwt-provider.fetchPowerSyncCredentials()`
//     (Bearer-токен StudyHub access, silent refresh за 5 мин до expiry, retry on 401)
//   - uploadData() — CRUD-очередь группируется по `transactionId` PowerSync,
//     каждая клиентская транзакция уходит отдельным POST на
//     `/api/sync/powersync/write` — серверная транзакция batch'а соответствует
//     ровно одной клиентской. Autocommit-операции (без transactionId) идут
//     ОДНИМ батчем, а не по запросу на каждую: при часовой offline-сессии
//     автосейва их набегают сотни, и пооперационная отправка упиралась в
//     rate-limit эндпоинта (60/мин) — очередь не могла пролиться в принципе.
//
// Capacitor: тот же connector, разница только в Database adapter
// (web → WASM SQLite + IndexedDB VFS, capacitor → @powersync/capacitor).
import type {
	CommonPowerSyncDatabase,
	CrudEntry,
	PowerSyncBackendConnector,
} from "@powersync/common";
import { uuidv7 } from "uuidv7";
import { captureClientError } from "@/lib/observability";
import { fetchPowerSyncCredentials, resetPowerSyncCredentials } from "./jwt-provider.js";
import { readPowerSyncOwner } from "./owner.js";
import {
	bumpAttempt,
	classifyWriteStatus,
	clearAttempts,
	type DroppedOperation,
	journalSyncFailure,
	MAX_UPLOAD_ATTEMPTS,
	notifyDropped,
} from "./upload-failures.js";
import {
	MAX_OPS_PER_REQUEST,
	PowerSyncWriteUnauthorized,
	postWriteOperations,
	type WriteOperationOut,
} from "./write-endpoint.js";

/**
 * Стабильный идемпотентный ключ операции: один и тот же CRUD-entry при retry
 * получает тот же ключ, иначе каждый HTTP обходил серверный idempotency-кэш и
 * дублировал PATCH/afterCommit при частичном отказе батча.
 *
 * SHA-256 (128 старших бит) вместо прежнего FNV-1a: тот давал 32 бита реальной
 * энтропии, размазанные по «128-битному» hex. При ~65k операций в пределах
 * user_id и 24-часового TTL кэша коллизия возвращала закэшированный ответ ЧУЖОЙ
 * операции — она объявлялась успешной и не выполнялась.
 */
async function stableIdempotencyKey(entry: CrudEntry): Promise<string> {
	const clientId =
		typeof (entry as { clientId?: unknown }).clientId === "number"
			? (entry as { clientId: number }).clientId
			: null;
	const raw = `${entry.table}\0${entry.id}\0${entry.op}\0${clientId ?? ""}\0${
		entry.transactionId ?? ""
	}`;
	const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(raw));
	const hex = [...new Uint8Array(digest).slice(0, 16)]
		.map((b) => b.toString(16).padStart(2, "0"))
		.join("");
	// UUID-образная форма (сервер валидирует ключ как строку, но храним читаемо).
	return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-5${hex.slice(13, 16)}-a${hex.slice(17, 20)}-${hex.slice(20, 32)}`;
}

async function toOperation(entry: CrudEntry, clientTimestamp: number): Promise<WriteOperationOut> {
	let idempotencyKey: string;
	try {
		idempotencyKey = await stableIdempotencyKey(entry);
	} catch {
		idempotencyKey = uuidv7();
	}
	const base = {
		table: entry.table,
		id: entry.id,
		idempotencyKey,
		clientTimestamp,
	} as const;
	if (entry.op === "PUT" || entry.op === "PATCH") {
		return {
			...base,
			op: entry.op,
			data: (entry.opData ?? {}) as Record<string, unknown>,
		};
	}
	return { ...base, op: "DELETE" };
}

/**
 * Группировка CRUD-очереди для отправки.
 *
 * Операции одной клиентской транзакции обязаны уехать вместе — серверная
 * транзакция должна совпадать с клиентской, иначе частичный fail оставит
 * несогласованное состояние. Autocommit-операции такой связи не имеют, поэтому
 * соседние склеиваем в один запрос до лимита его размера.
 *
 * Порядок очереди сохраняется: PowerSync отдаёт CRUD в last-write-wins
 * последовательности, и отправка autocommit-операций после транзакционных
 * переставила бы, например, INSERT после зависящего от него UPDATE.
 */
function groupEntries(entries: readonly CrudEntry[]): Array<{ key: string; ops: CrudEntry[] }> {
	const groups: Array<{ key: string; ops: CrudEntry[] }> = [];
	const byTransaction = new Map<string, { key: string; ops: CrudEntry[] }>();
	let openAutocommit: { key: string; ops: CrudEntry[] } | null = null;

	for (const entry of entries) {
		if (entry.transactionId != null) {
			const key = `tx_${String(entry.transactionId)}`;
			const existing = byTransaction.get(key);
			if (existing) {
				existing.ops.push(entry);
			} else {
				const group = { key, ops: [entry] };
				byTransaction.set(key, group);
				groups.push(group);
			}
			openAutocommit = null;
			continue;
		}
		if (!openAutocommit || openAutocommit.ops.length >= MAX_OPS_PER_REQUEST) {
			openAutocommit = { key: `autocommit_${groups.length}`, ops: [] };
			groups.push(openAutocommit);
		}
		openAutocommit.ops.push(entry);
	}
	return groups;
}

/** Операция, которую разбирать больше не будем: отдана в журнал и подтверждена. */
interface SettledFailure {
	operation: WriteOperationOut;
	status: string;
	attempts: number;
	kind: ReturnType<typeof classifyWriteStatus>["kind"];
	reason: ReturnType<typeof classifyWriteStatus>["reason"];
}

export class StudyHubConnector implements PowerSyncBackendConnector {
	async fetchCredentials() {
		const { token, endpoint } = await fetchPowerSyncCredentials();
		return { token, endpoint };
	}

	/**
	 * SDK зовёт этот метод, когда сервис PowerSync ответил 401 (токен отвергнут:
	 * протух, выписан прошлому аккаунту, сменился ключ подписи). Без него наш
	 * кэш в `jwt-provider` продолжал отдавать ТОТ ЖЕ токен на каждый повторный
	 * `fetchCredentials()` — SDK крутил 401 по кругу, статус залипал в
	 * `connecting`, полоска «Синхронизация библиотеки…» не гасла и данные не
	 * приезжали до перезагрузки страницы.
	 *
	 * Метода нет в типе `PowerSyncBackendConnector` (проверено на
	 * @powersync/common 2.2.0 — там только `fetchCredentials` / `uploadData` /
	 * `postCheckpointRequest`), но рантайм зовёт его как опциональный:
	 * `this.connector.invalidateCredentials?.()` в
	 * `AbstractRemote.invalidateCredentials` (@powersync/shared-internals 1.2.0),
	 * куда ведут и 401 на запросе, и инструкция ядра `FetchCredentials`. Это не
	 * мёртвый код — не удалять.
	 */
	invalidateCredentials(): void {
		resetPowerSyncCredentials();
	}

	async uploadData(database: CommonPowerSyncDatabase): Promise<void> {
		const batch = await database.getCrudBatch(MAX_OPS_PER_REQUEST);
		if (!batch) return;

		const clientTimestamp = Date.now();
		const settledKeys: string[] = [];
		const failures: SettledFailure[] = [];
		const ownerId = readPowerSyncOwner();
		let retryable = false;

		for (const group of groupEntries(batch.crud)) {
			const operations = await Promise.all(
				group.ops.map((entry) => toOperation(entry, clientTimestamp)),
			);

			let results: Awaited<ReturnType<typeof postWriteOperations>>;
			try {
				results = await postWriteOperations(operations, group.key);
			} catch (err) {
				// 401 — сбрасываем кэш кредов, следующий fetchCredentials возьмёт
				// свежие. Батч не подтверждаем: PowerSync повторит через backoff.
				if (err instanceof PowerSyncWriteUnauthorized) resetPowerSyncCredentials();
				throw err;
			}

			// `postWriteOperations` уже сверил длины массивов, поэтому пара всегда
			// полная — но берём её через zip без non-null assertion.
			for (const [result, operation] of results.map((r, i) => [r, operations[i]] as const)) {
				if (!operation) continue;
				if (result.status === "success") {
					settledKeys.push(operation.idempotencyKey);
					continue;
				}

				const { kind, reason } = classifyWriteStatus(result.status);

				// Мгновенно отбрасываем ТОЛЬКО forbidden: CASL_DENIED перманентен,
				// повтор ничего не изменит.
				//
				// `invalid` и `conflict` — временные по своей природе, и счётчик
				// попыток им нужен ровно так же, как сетевым ошибкам. Клиент со
				// свежей таблицей в схеме до выката сервера получает `invalid`
				// («table not registered»), а `conflict` возвращают handler'ы,
				// когда родительская строка ещё не доехала. Отбрасывать такую
				// правку с первой попытки — терять данные там, где достаточно было
				// подождать минуту.
				//
				// ЦЕНА этого обмена — head-of-line blocking, и она реальная.
				// Очередь PowerSync подтверждается только ПРЕФИКСОМ:
				// `batch.complete()` делает `DELETE FROM ps_crud WHERE id <= ?`
				// (`SqliteBucketStorage.handleCrudCheckpoint`), пропустить
				// операцию в середине физически нечем. Поэтому одна операция,
				// невалидная навсегда, задерживает ВСЕ последующие правки
				// пользователя на свои 5 циклов backoff — то есть минуты, а не
				// секунды. Сокращать окно можно только различением «невалидна
				// навсегда» и «невалидна сейчас» на стороне сервера (сейчас
				// оба случая приезжают одним статусом `invalid`), а не здесь.
				if (kind === "permanent" && reason === "forbidden") {
					settledKeys.push(operation.idempotencyKey);
					failures.push({ operation, status: result.status, attempts: 1, kind, reason });
					continue;
				}

				const attempts = bumpAttempt(operation.idempotencyKey);
				if (attempts < MAX_UPLOAD_ATTEMPTS) {
					retryable = true;
					continue;
				}
				settledKeys.push(operation.idempotencyKey);
				failures.push({ operation, status: result.status, attempts, kind, reason });
			}
		}

		// Журналируем и оповещаем ТОЛЬКО когда батч действительно закрывается.
		//
		// Раньше запись в журнал шла прямо в цикле разбора результатов: если в
		// батче рядом с перманентным отказом жила transient-операция, `retryable`
		// поднимал исключение до `batch.complete()`, PowerSync повторял тот же
		// батч — и тот же отказ журналировался снова, до пяти одинаковых строк на
		// одну реальную потерю.
		if (retryable) {
			// Не подтверждаем батч — PowerSync повторит его через backoff.
			throw new Error("powersync.write: часть операций отклонена, ожидаем повтор");
		}

		const dropped = await this.journalFailures(failures, ownerId);

		if (dropped.length > 0) {
			captureClientError(new Error(`powersync.write: отброшено операций — ${dropped.length}`), {
				count: dropped.length,
				reasons: [...new Set(dropped.map((op) => op.status))],
			});
			notifyDropped(dropped);
		}

		// Батч подтверждён — снимаем счётчики попыток по всем его операциям.
		clearAttempts(settledKeys);
		await batch.complete();
	}

	/**
	 * Запись отказов в owner-scoped журнал.
	 *
	 * Без владельца журналировать нельзя: `listSyncFailures(user.id)` такие
	 * записи никогда не покажет, и запись о тихой потере сама потеряется тихо.
	 * Раньше сюда подставлялась строка `"unknown"` — ровно этот случай.
	 *
	 * Вызывается ДО `batch.complete()` — и это обязательное условие: тело
	 * операции (`operation.data`) сохраняется как снимок для повтора, потому
	 * что после закрытия батча локальная строка будет ре-материализована из
	 * серверного состояния (разбор — в шапке `upload-failures.ts`).
	 */
	private async journalFailures(
		failures: readonly SettledFailure[],
		ownerId: string | null,
	): Promise<DroppedOperation[]> {
		if (failures.length === 0) return [];
		const dropped = failures.map(
			(failure): DroppedOperation => ({
				table: failure.operation.table,
				id: failure.operation.id,
				op: failure.operation.op,
				status: failure.status,
				attempts: failure.attempts,
			}),
		);
		if (!ownerId) {
			captureClientError(new Error("powersync.write: отказ без владельца локальной базы"), {
				count: failures.length,
				tables: [...new Set(dropped.map((op) => op.table))],
			});
			return dropped;
		}
		for (const failure of failures) {
			await journalSyncFailure({
				ownerId,
				table: failure.operation.table,
				entityId: failure.operation.id,
				op: failure.operation.op,
				kind: failure.kind,
				reason: failure.reason,
				payload: failure.operation.op === "DELETE" ? null : (failure.operation.data ?? null),
			});
		}
		return dropped;
	}
}
